import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, MessageCircleOff, BookX, Timer, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { DjayToetsAvatar } from '../../mascot';
import { DOMAIN_META } from '../utils/assessmentDomainConfig';
import type { AssessmentDomainId } from '../types';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
interface AssessmentIntroScreenProps {
  domainId: AssessmentDomainId;
  questionCount: number;
  onStart: () => void;
  onCancel: () => void;
}
export function AssessmentIntroScreen({
  domainId,
  questionCount,
  onStart,
  onCancel
}: AssessmentIntroScreenProps) {
  const meta = DOMAIN_META[domainId];
  const minutesPerQuestion = domainId === 'rekenen' ? 1.4 : 1.2;
  const estimatedMinutes = Math.round(questionCount * minutesPerQuestion);
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans text-slate-800" data-id="element-654">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="w-full max-w-md text-center" data-id="element-655">
        <motion.div initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        delay: 0.15,
        type: 'spring',
        bounce: 0.4
      }} className="flex justify-center mb-6" data-id="element-656">
          <DjayToetsAvatar domain={domainId} size="lg" animate data-id="element-657" />
        </motion.div>

        <h1 className="text-3xl font-black text-slate-900 mb-2 tracking-tight" data-id="element-658">
          {meta.label}
        </h1>
        <p className="text-slate-500 text-sm font-medium mb-8" data-id="element-659">Toets</p>

        <div className="flex items-center justify-center gap-6 text-slate-600 font-medium mb-10" data-id="element-660">
          <div className="flex items-center" data-id="element-661">
            <CheckCircle2 className="w-4 h-4 mr-2 text-slate-400" data-id="element-662" />
            {questionCount} vragen
          </div>
          <div className="flex items-center" data-id="element-663">
            <Clock className="w-4 h-4 mr-2 text-slate-400" data-id="element-664" />~{estimatedMinutes}{' '}
            minuten
          </div>
        </div>

        <div className="space-y-4 mb-10 text-left inline-block" data-id="element-665">
          <div className="flex items-center gap-4" data-id="element-666">
            <MessageCircleOff className="w-5 h-5 text-slate-400 shrink-0" data-id="element-667" />
            <p className="text-slate-600 font-medium" data-id="element-668">
              Geen feedback na elke vraag
            </p>
          </div>
          <div className="flex items-center gap-4" data-id="element-669">
            <BookX className="w-5 h-5 text-slate-400 shrink-0" data-id="element-670" />
            <p className="text-slate-600 font-medium" data-id="element-671">
              Geen spiekbriefje of hulp
            </p>
          </div>
          <div className="flex items-center gap-4" data-id="element-672">
            <Timer className="w-5 h-5 text-slate-400 shrink-0" data-id="element-673" />
            <p className="text-slate-600 font-medium" data-id="element-674">Neem rustig de tijd</p>
          </div>
        </div>

        <Button onClick={onStart} size="xl" fullWidth className="bg-orange-500 text-white hover:bg-orange-600 shadow-xl rounded-2xl text-fluid-lg font-bold mb-6" data-id="element-675">
          <span className="mr-2" data-id="element-676">Ik ben er klaar voor</span>
          <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-677" />
        </Button>

        <button onClick={onCancel} className="text-sm text-slate-400 hover:text-slate-600 font-medium transition-colors" data-id="element-678">
          Annuleren en terug
        </button>
      </motion.div>
    </div>;
}